import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KEY = "bln_char_classes";
const CLASSES = ["Minor", "Major", "Critical"];

export default function BlnSettingsPanel() {
  const [rules, setRules] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(rules)); } catch { /* ignore */ } }, [rules]);
  const [adding, setAdding] = useState(false);
  const [tol, setTol] = useState("");
  const [cls, setCls] = useState("Minor");

  function addRule() {
    const t = parseFloat(tol);
    if (!(t > 0)) { toast.error("Enter a tolerance greater than 0"); return; }
    const next = [...rules.filter((r) => !(r.tol === t)), { tol: t, cls }].sort((a, b) => b.tol - a.tol);
    setRules(next); setAdding(false); setTol(""); setCls("Minor");
    toast.success("Rule added");
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-800">Settings</h1>
      <p className="text-sm text-slate-500 mb-6">AAMPL — Advanced Anmol Metcomp Pvt Ltd.</p>

      <div className="border border-slate-200 rounded-xl bg-white">
        <div className="p-5 border-b border-slate-100">
          <div className="text-lg font-semibold text-slate-800">Character Classes</div>
          <div className="text-sm text-slate-500">Assign character classes to dimensions with specific tolerances.</div>
        </div>
        <div className="p-5">
          {rules.length === 0 ? (
            <>
              <div className="font-semibold text-slate-800">No rules yet</div>
              <div className="text-sm text-slate-500 mt-1 max-w-sm">Click 'Add rule' to introduce a new tolerance-based character class.</div>
              <div className="mt-4 border border-slate-200 rounded-lg p-4 max-w-xs bg-slate-50">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Example</div>
                <div className="flex justify-between text-sm text-slate-600"><span>± 0.50 mm</span><span>Minor</span></div>
                <div className="flex justify-between text-sm text-slate-600"><span>± 0.20 mm</span><span>Major</span></div>
              </div>
            </>
          ) : (
            <div className="space-y-2 max-w-md">
              <div className="text-xs text-slate-500">A dimension's tolerance ≤ the value gets that class (tightest wins).</div>
              {rules.map((r) => (
                <div key={r.tol} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2">
                  <span className="font-mono text-sm text-slate-700">± {r.tol.toFixed(2)} mm</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${r.cls === "Critical" ? "bg-red-50 text-red-700" : r.cls === "Major" ? "bg-purple-50 text-purple-700" : "bg-amber-50 text-amber-700"}`}>{r.cls}</span>
                  <button onClick={() => setRules(rules.filter((x) => x.tol !== r.tol))} className="ml-auto text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}

          {adding && (
            <div className="mt-4 flex items-end gap-2 flex-wrap max-w-md">
              <label className="text-sm">
                <div className="text-xs text-slate-500">Tolerance (± mm)</div>
                <input type="number" step="0.01" value={tol} onChange={(e) => setTol(e.target.value)} placeholder="0.50" className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm w-28" />
              </label>
              <label className="text-sm">
                <div className="text-xs text-slate-500">Class</div>
                <select value={cls} onChange={(e) => setCls(e.target.value)} className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm">
                  {CLASSES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
              <button onClick={addRule} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md px-3 py-1.5">Save</button>
              <button onClick={() => setAdding(false)} className="border border-slate-300 text-sm rounded-md px-3 py-1.5 hover:bg-slate-50">Cancel</button>
            </div>
          )}

          {!adding && (
            <button onClick={() => setAdding(true)} className="mt-4 inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-3 py-2">
              <Plus size={15} /> Add rule
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
